import React from 'react';
import { NpsFace, npsClassification, npsLevel } from '@/components/NpsFace';

const NPS_TONE = {
  promoter: 'bg-[#ECFDF5] text-[#047857] border-[#A7F3D0]',
  passive: 'bg-[#FFFBEB] text-[#92400E] border-[#FDE68A]',
  detractor: 'bg-[#FEF2F2] text-[#B91C1C] border-[#FECACA]',
};
const SENTIMENT_TONE = {
  positive: 'bg-[#E9F7EF] text-[#078454] border-[#C7EAD7]',
  neutral: 'bg-[#F1F5F9] text-[#475569] border-[#E2E8F0]',
  negative: 'bg-[#FFF0F0] text-[#B3262E] border-[#F1C7CA]',
};

const Card = ({ label, children, testId }) => (
  <div className="bg-[#F7F9FB] border border-[#EDF0F4] rounded-2xl p-5 min-w-0" data-testid={testId}>
    <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-[#64748B] mb-3">{label}</p>
    {children}
  </div>
);

// Per-response NPS presentation snapshot; legacy responses keep the colour face.
const npsPres = (resp) => {
  const p = Object.values(resp?.nps_presentation || {})[0];
  return { showFace: !p || p.style !== 'numbers', appearance: p?.appearance || 'color' };
};

export function NpsSummary({ response }) {
  if (response.nps_score == null) return null;
  const score = Math.round(Number(response.nps_score));
  const pres = npsPres(response);
  return (
    <Card label="NPS®" testId="summary-nps">
      <div className="flex items-center gap-4">
        {pres.showFace && <NpsFace score={score} appearance={pres.appearance} size={52} />}
        <div>
          <p className="text-4xl font-bold text-[#0F172A] font-['Montserrat'] leading-none" data-testid="summary-nps-score">{score}</p>
          <span className={`inline-block mt-2 text-xs font-semibold px-2.5 py-1 rounded-full border ${NPS_TONE[npsLevel(score)]}`} data-testid="summary-nps-class">
            {npsClassification(score)}
          </span>
        </div>
      </div>
    </Card>
  );
}

export function SentimentSummary({ response, hasFeedback }) {
  const s = response.sentiment;
  const kws = Array.isArray(response.keywords) ? response.keywords.slice(0, 6) : [];
  return (
    <Card label="Feedback Sentiment" testId="summary-sentiment">
      {s ? (
        <span className={`inline-flex text-sm font-bold px-3 py-1.5 rounded-full border ${SENTIMENT_TONE[s] || SENTIMENT_TONE.neutral}`} data-testid="summary-sentiment-value">
          {s.charAt(0).toUpperCase() + s.slice(1)}
        </span>
      ) : (
        <span className="text-sm text-[#94A3B8]" data-testid="summary-sentiment-value">{hasFeedback ? 'Not analysed yet' : 'No written or voice feedback'}</span>
      )}
      <p className="text-xs text-[#64748B] mt-3" data-testid="summary-sentiment-helper">Based on the respondent’s written or transcribed feedback.</p>
      {kws.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-3" data-testid="summary-keywords">
          {kws.map((k, i) => <span key={`${k}-${i}`} className="text-[11px] text-[#475569] bg-white border border-[#E2E8F0] rounded-full px-2.5 py-0.5">{k}</span>)}
        </div>
      )}
    </Card>
  );
}

export function ClassificationSummary({ items }) {
  return items.map((c) => (
    <Card key={c.question_id} label={c.name} testId={`summary-classification-${c.question_id}`}>
      <p className="text-2xl font-bold text-[#0F172A] font-['Montserrat']" data-testid={`summary-classification-value-${c.question_id}`}>{c.label}</p>
      <p className="text-xs text-[#64748B] mt-2 line-clamp-2">From: “{c.answer}”</p>
    </Card>
  ));
}
